/**
 * CookieConsent — banner bottom-sheet no bloqueante (Ley 1581/2012 CO).
 *
 * Card 71tCkSkN — OBS F0.5 CookieConsent banner + modal.
 *
 * Comportamiento
 * --------------
 * - Aparece SOLO cuando `needsConsentDecision()` es `true` (no hay
 *   registro persistido, o la versión almacenada es stale). La
 *   suscripción al evento `consent-changed` (a través de `useConsent`)
 *   hace que el banner se oculte automáticamente en cuanto se persiste
 *   una decisión — sin polling y sin flags locales duplicados.
 * - Dos botones primarios:
 *     · "Aceptar todo"     → analytics + marketing = 'granted'
 *     · "Solo esenciales"  → analytics + marketing = 'denied'
 * - Link "Configurar" abre el `ConsentModal` con toggles granulares.
 * - No bloquea la interacción con la página (fixed bottom-0, sin
 *   backdrop). El backdrop / bloqueo solo aparece cuando se abre el
 *   modal de configuración.
 *
 * Integración backend
 * -------------------
 * En esta fase (F0.5) solo persiste en `localStorage` vía el módulo
 * `lib/analytics/consent`. La sincronización con `POST /users/me/consent`
 * llega en F0.6 y NO forma parte de esta card.
 */
import { useState } from 'react';

import { useConsent } from '@/hooks/useConsent';
import { needsConsentDecision } from '@/lib/analytics/consent';

import ConsentModal from './ConsentModal';

export default function CookieConsent() {
  const { consent, setConsent } = useConsent();
  const [modalOpen, setModalOpen] = useState(false);

  // El banner es una función pura del estado persistido: mientras no haya
  // un registro válido, mostramos. Al persistir vía `setConsent`, el
  // suscriptor de `consent-changed` en `useConsent` actualiza `consent`
  // y `needsConsentDecision` pasa a `false` en el próximo render.
  const needsDecision = consent === null && needsConsentDecision();
  if (!needsDecision && !modalOpen) return null;

  const acceptAll = (): void => {
    setConsent({ analytics: 'granted', marketing: 'granted' });
  };

  const rejectAll = (): void => {
    setConsent({ analytics: 'denied', marketing: 'denied' });
  };

  return (
    <>
      {needsDecision && (
        <div
          role="region"
          aria-label="Aviso de cookies"
          className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                Usamos cookies para mejorar tu experiencia.
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Podés aceptar todas o elegir solo las esenciales.{' '}
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="underline text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  Configurar
                </button>
              </p>
            </div>
            <div className="flex gap-2 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={rejectAll}
                className="flex-1 sm:flex-none inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Solo esenciales
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="flex-1 sm:flex-none inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Aceptar todo
              </button>
            </div>
          </div>
        </div>
      )}

      <ConsentModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        initialAnalytics={consent?.analytics ?? 'denied'}
        initialMarketing={consent?.marketing ?? 'denied'}
      />
    </>
  );
}
